import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import './styles.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GameToolbar from '../../components/whoispaying/GameToolbar';
import RulesModal from '../../components/whoispaying/RulesModal';
// Removed inline banner; show popup instead
import WinnerPopup from '../../components/whoispaying/WinnerPopup';
import RoundRevealOverlay from '../../components/whoispaying/RoundRevealOverlay';
import GlobalNav from '../../components/GlobalNav';
import ExpirationCountdown from '../../components/ExpirationCountdown';
import ExpiredNotice from '../../components/ExpiredNotice';
import '../../components/whoispaying/reveal.css';
import { isFeatureOn } from '../../utils/featureFlags';
import { useRoomEngine } from '../../hooks/useRoomEngine';
import { useViewport } from '../../hooks/useViewport';
import { api as firestoreApi, listenToRoom, listenToPlayers, hasMembership } from '../../utils/firestoreApi';
import { decodeRoomId } from '../../utils/roomUtils';
import { storeExpiryForRoom } from '../../utils/storage';
import { listGames } from '../../components/whoispaying/GameRegistry';
import { getDefaultConfig } from '../../components/whoispaying/games';

const coerceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  try {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  } catch (err) {
    return null;
  }
};

export default function WhoIsPayingRoom() {
  const { roomId: roomIdParam } = useParams();
  const { roomId, hostId } = useMemo(() => decodeRoomId(roomIdParam), [roomIdParam]);
  const navigate = useNavigate();
  const location = useLocation();
  const viewport = useViewport();
  const [session, setSession] = useState(location.state || null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [wizardStep, setWizardStep] = useState(null);
  const [wizardSeen, setWizardSeen] = useState(false);
  const [guestWizardStep, setGuestWizardStep] = useState(null);
  const [guestWizardSeen, setGuestWizardSeen] = useState(false);

  // Fetch session if direct link
  useEffect(() => {
    if (!session && roomId) {
      setSessionLoading(true);
      firestoreApi('sync', { roomId })
        .then((result) => {
          const award = result.award || result.room?.award || '';
          const hostName = result.hostName || result.room?.hostName || 'Host';
          const expiresAt = result.expiresAt || result.room?.expiresAt || null;
          
          // Check if user is a member
          if (result.isMember && result.playerId) {
            // User is a member - restore their session
            console.log('[WhoIsPayingRoom] User is a member, restoring session');
            setSession({ 
              roomId, 
              playerId: result.playerId, 
              displayName: result.playerName || 'Player',
              hostName, 
              hostId: result.hostId, 
              award, 
              expiresAt 
            });
          } else {
            // User is not a member - they need to rejoin
            console.log('[WhoIsPayingRoom] User is not a member of this room');
            setSession({ roomId, playerId: null, displayName: null, hostName, hostId: result.hostId, award, expiresAt });
            setError('You are not a member of this room. Please rejoin using the entry page.');
          }
        })
        .catch((err) => {
          console.error('[WhoIsPayingRoom] Sync failed:', err);
          setError(err?.message || 'Failed to load room');
          setSession(null);
        })
        .finally(() => setSessionLoading(false));
    }
  }, [session, roomId, hostId]);

  const [room, setRoom] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const games = listGames();
  const gameLookup = new Map(games.map((g) => [g.key, g]));
  const [rulesOpen, setRulesOpen] = useState(false);
  const [hostActionLoading, setHostActionLoading] = useState(false);
  const [gameConfig, setGameConfig] = useState({});
  const [gameConfigKey, setGameConfigKey] = useState(null);
  const [toolbarAward, setToolbarAward] = useState('');
  const [awardDirty, setAwardDirty] = useState(false);
  const [syncStatus, setSyncStatus] = useState('ok');
  const [lastSync, setLastSync] = useState(Date.now());
  const [copiedRoom, setCopiedRoom] = useState(false);
  const copyRoomTimerRef = useRef(null);
  const playersUnsubRef = useRef(null);
  const playersRetryTimerRef = useRef(null);
  const playersRetryCountRef = useRef(0);
  const playersListenerStartedRef = useRef(false);
  const playersHadDataRef = useRef(false);
  const [playersCountRT, setPlayersCountRT] = useState(null);
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [wizardCopied, setWizardCopied] = useState(false);
  // Only show developer debug panels in development builds
  const showDebugPlayers = Boolean(import.meta?.env?.DEV);

  const engine = useRoomEngine(room, session?.playerId, [hostId, session?.hostId]);
  const isHost = Boolean(engine?.isHost);

  // Host onboarding wizard overlay (shown once after room creation via entry page)
  useEffect(() => {
    if (!wizardStep && !wizardSeen && isHost && location.state && location.state.showWizard) {
      setWizardStep(2);
      setWizardSeen(true);
    }
  }, [wizardStep, wizardSeen, isHost, location.state]);
  const phase = engine?.phase ?? 'lobby';

  const activeGame = useMemo(() => {
    if (!engine?.summary?.key) {
      return engine?.summary || null;
    }
    const base = games.find((game) => game.key === engine.summary.key);
    return base ? { ...base, ...engine.summary } : engine.summary;
  }, [engine, games]);

  const expiresAt = useMemo(() => {
    const sources = [roomState?.expiresAt, roomState?.room?.expiresAt, session?.expiresAt].filter(Boolean);
    const derived = sources.length ? sources.map(coerceDate).find(Boolean) : null;
    if (roomId && derived) {
      storeExpiryForRoom(roomId, derived);
    }
    return derived;
  }, [roomState, session?.expiresAt, roomId]);
  const isExpired = useMemo(() => {
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() <= Date.now();
  }, [expiresAt]);
  const [expiredOpen, setExpiredOpen] = useState(false);

  useEffect(() => {
    // Open the expired notice when we detect expiration
    setExpiredOpen(Boolean(isExpired));
  }, [isExpired]);

  useEffect(() => () => {
    if (copyRoomTimerRef.current) {
      clearTimeout(copyRoomTimerRef.current);
      copyRoomTimerRef.current = null;
    }
  }, [roomId]);

  const handleCopyRoom = useCallback(async () => {
    if (!roomId) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(roomId));
      }
      setCopiedRoom(true);
    } catch {
      setCopiedRoom(true);
    } finally {
      if (copyRoomTimerRef.current) clearTimeout(copyRoomTimerRef.current);
      copyRoomTimerRef.current = setTimeout(() => setCopiedRoom(false), 1500);
    }
  }, [roomId]);

  const applyRoomResult = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') return false;

      const rawRoom = payload.room ?? (payload.roomId && typeof payload.roomId === 'string' ? payload : null);

      setRoomState((previous) => {
        const previousSnapshot = previous ?? null;
        const previousRoom = previousSnapshot?.room ?? (previousSnapshot && !previousSnapshot.room ? previousSnapshot : null);

        let nextRoom = rawRoom ? { ...(rawRoom || {}) } : previousRoom ? { ...(previousRoom || {}) } : {};
        // Preserve players if payload does not contain players
        const incomingHasPlayers = Boolean(rawRoom && Object.prototype.hasOwnProperty.call(rawRoom, 'players'));
        if (!incomingHasPlayers && previousRoom && Array.isArray(previousRoom.players)) {
          nextRoom.players = previousRoom.players;
        }

        if (payload.game && typeof payload.game === 'object') {
          nextRoom = { ...(nextRoom || {}), game: { ...(nextRoom?.game || {}), ...payload.game } };
        }

        const nextState = { ...(previousSnapshot || {}), ...payload, room: nextRoom, game: nextRoom?.game ?? payload.game ?? previousSnapshot?.game ?? null };
        return nextState;
      });

      setRoom((prev) => {
        const prevRoom = prev || {};
        const incomingRoom = payload.room ? payload.room : payload;
        if (!incomingRoom) return prevRoom;
        const hasPlayers = Object.prototype.hasOwnProperty.call(incomingRoom, 'players');
        return {
          ...prevRoom,
          ...incomingRoom,
          ...(hasPlayers ? {} : (Array.isArray(prevRoom.players) ? { players: prevRoom.players } : {}))
        };
      });
      return true;
    },
    []
  );

  // Real-time Firestore listeners (replaces polling)
  useEffect(() => {
    if (!roomId) return;
    
    // Don't start listeners until session is loaded (on refresh, wait for session restoration)
    if (!session && !sessionLoading) {
      console.log('[WhoIsPayingRoom] Waiting for session before starting listeners');
      return;
    }
    
    // Don't start listeners if user is not a valid member
    if (!session?.playerId) {
      console.log('[WhoIsPayingRoom] No valid playerId in session, skipping listeners');
      return;
    }

    setLoading(true);
    setPlayersCountRT(null);
    setPlayersLoaded(false);
    playersListenerStartedRef.current = false;

    console.log('[WhoIsPayingRoom] Setting up listeners for room:', roomId, 'playerId:', session.playerId);

    // Room listener
    const unsubscribeRoom = listenToRoom(roomId, (result, error) => {
      if (error) {
        setSyncStatus('error');
        setError((prev) => prev || error.message || 'Connection issue');
        setLoading(false);
        return;
      }

      if (!result) return;

      setSyncStatus('ok');
      setLastSync(Date.now());
      applyRoomResult({ room: { ...(result.room || {}) } });
    });

    // Players listener - separate from room listener to avoid race conditions
    const startPlayersListener = () => {
      console.log('[WhoIsPayingRoom] Starting players listener for room:', roomId);
      
      const unsubscribePlayers = listenToPlayers(roomId, (players, pError) => {
        if (pError) {
          if (pError.code === 'permission-denied') {
            console.warn('[WhoIsPayingRoom] Permission denied for players listener');
            // Only show error if we previously had data - ignore initial permission issues
            if (playersHadDataRef.current) {
              setError('Connection lost. Please rejoin the room.');
              setPlayersLoaded(false);
            } else {
              // First load permission issue - might not be a member yet
              console.log('[WhoIsPayingRoom] Initial permission denied - user may not be a member yet');
            }
            return;
          }
          console.error('[WhoIsPayingRoom] Players listener error:', pError);
          if (!playersHadDataRef.current) setPlayersLoaded(false);
          setError((prev) => prev || (pError.message || 'Unable to read players'));
          return;
        }

        console.log('[WhoIsPayingRoom] Players update received:', players.length, 'players');
        console.log('[WhoIsPayingRoom] Debug: Raw Players List', JSON.stringify(players, null, 2));

        // Update state with new players list
        setRoom((prev) => ({
          ...prev,
          players: players
        }));
        setPlayersCountRT(Array.isArray(players) ? players.length : 0);
        playersHadDataRef.current = Array.isArray(players) && players.length > 0;
        setPlayersLoaded(true);
        setLoading(false);
        // Clear any previous errors on successful load
        setError('');
      });
      
      playersUnsubRef.current = unsubscribePlayers;
    };

    // Start players listener immediately (not inside room listener callback)
    startPlayersListener();

    return () => {
      console.log('[WhoIsPayingRoom] Cleaning up listeners for room:', roomId);
      unsubscribeRoom();
      if (typeof playersUnsubRef.current === 'function') {
        try { playersUnsubRef.current(); } catch {}
        playersUnsubRef.current = null;
      }
      if (playersRetryTimerRef.current) {
        clearTimeout(playersRetryTimerRef.current);
        playersRetryTimerRef.current = null;
      }
      playersRetryCountRef.current = 0;
      playersListenerStartedRef.current = false;
      playersHadDataRef.current = false;
    };
  }, [roomId, session, sessionLoading, applyRoomResult]);

  const playerId = session?.playerId;
  const players = useMemo(() => (Array.isArray(room?.players) ? room.players : []), [room]);
  // Prefer the most reliable source, but avoid flashing 0 before listeners load
  const roomsize = room?.Roomsize ?? roomState?.Roomsize ?? null;
  const computedPlayerCount = useMemo(() => {
    const candidates = [
      typeof playersCountRT === 'number' ? playersCountRT : null,
      Array.isArray(players) ? players.length : null,
      typeof roomsize === 'number' ? roomsize : null
    ].filter((n) => typeof n === 'number');
    return candidates.length ? Math.max(...candidates) : 0;
  }, [playersCountRT, players, roomsize]);
  const playerCountDisplay = playersLoaded ? computedPlayerCount : '–';
  const roomHostId = room?.hostId ?? roomState?.hostId ?? hostId ?? null;
  const roomAward = room?.award ?? roomState?.award ?? session?.award ?? null;

  // Derive host name from players list (fallback to session/"Host")
  const hostName = useMemo(() => {
    if (!roomHostId) return session?.hostName || 'Host';
    const hostPlayer = players.find((p) => p.playerId === roomHostId);
    return hostPlayer?.name || session?.hostName || 'Host';
  }, [players, roomHostId, session?.hostName]);

  const buildDefaultConfig = getDefaultConfig;

  const selectedGameKey = useMemo(() => room?.game?.key || engine?.summary?.key || gameConfigKey, [room?.game?.key, engine?.summary?.key, gameConfigKey]);

  const activeGameSchema = useMemo(() => {
    const key = selectedGameKey || engine?.summary?.key;
    if (!key) return {};
    const entry = gameLookup.get(key);
    return entry?.config || {};
  }, [selectedGameKey, engine?.summary?.key, gameLookup]);

  // Ensure config values are visible even when inputs are disabled outside lobby
  const displayConfig = useMemo(() => {
    // --- CONFIG OVERRIDE: Number of Rounds is always 5 and hidden from UI ---
    // To change, update value here and in GameToolbar.jsx
    const schemaKeys = Object.keys(activeGameSchema || {});
    if (!schemaKeys.length) return gameConfig;
    const roomConfig = (room?.game && typeof room.game === 'object') ? room.game : {};
    const result = {};
    for (const k of schemaKeys) {
      if (k === 'Number_of_rounds') {
        result[k] = 5;
        continue;
      }
      // In lobby show the pending local config; otherwise prefer the locked room config
      result[k] = (phase === 'lobby')
        ? (gameConfig?.[k] ?? roomConfig?.[k] ?? '')
        : (roomConfig?.[k] ?? gameConfig?.[k] ?? '');
    }
    return result;
  }, [activeGameSchema, gameConfig, room?.game, phase]);

  const handleGameConfigChange = useCallback((field, value) => {
    setGameConfig((previous) => ({ ...previous, [field]: value }));
  }, []);

  const phaseLabel = useMemo(() => {
    const base = (engine?.round?.phase ?? phase).replace(/_/g, ' ');
    return base.charAt(0).toUpperCase() + base.slice(1);
  }, [engine?.round?.phase, phase]);

  useEffect(() => {
    if (!selectedGameKey) {
      if (gameConfigKey !== null) {
        setGameConfigKey(null);
        setGameConfig({});
      }
      return;
    }
  if (phase === 'locked' || phase === 'in_progress' || phase === 'revealed' || phase === 'expired') {
      if (gameConfigKey !== selectedGameKey) setGameConfigKey(selectedGameKey);
      return;
    }
    if (selectedGameKey !== gameConfigKey) {
      setGameConfig(buildDefaultConfig(selectedGameKey));
      setGameConfigKey(selectedGameKey);
    }
  }, [buildDefaultConfig, gameConfigKey, selectedGameKey, phase]);

  useEffect(() => {
    if (!awardDirty) setToolbarAward(roomAward ?? '');
  }, [awardDirty, roomAward]);

  useEffect(() => {
    if (awardDirty && (toolbarAward ?? '') === (roomAward ?? '')) setAwardDirty(false);
  }, [awardDirty, roomAward, toolbarAward]);

  const selectedGameEntry = useMemo(() => (selectedGameKey ? gameLookup.get(selectedGameKey) ?? null : null), [gameLookup, selectedGameKey]);

  const stageGame = useMemo(() => {
    let gameKey = selectedGameKey;
    let gameName = null;
    if (room?.game) {
      if (typeof room.game === 'string') {
        gameName = room.game.trim();
      } else {
        gameKey = room.game.key || gameKey;
        gameName = room.game.name || null;
      }
    }
    let metadata = gameKey ? gameLookup.get(gameKey) : null;
    if (!metadata && gameName) {
      metadata = games.find((g) => g.name.trim().toLowerCase() === gameName.trim().toLowerCase()) || null;
    }
    if (!metadata && games.length) metadata = games[0];
    const merged = { ...(metadata || {}) };
    if (room?.game && typeof room.game === 'object') Object.assign(merged, room.game);
    merged.key = merged.key || gameKey || null;
    return merged;
  }, [room?.game, selectedGameKey, gameLookup, games]);

  const round = engine?.round ?? null;

  const scoreboardRows = useMemo(() => {
    if (!players.length) return { rows: [], maxRounds: 1, winnerIds: [] };
    let maxRounds = 1;
    let maxTotal = -Infinity;
    players.forEach((player) => {
      let values = Array.isArray(player.cards) ? player.cards.map(Number) : [];
      if (!values.length) {
        for (let i = 1; i <= 9; i++) {
          const key = `Round${i}`;
          if (player[key] !== undefined && player[key] !== null && player[key] !== '' && !isNaN(Number(player[key]))) values.push(Number(player[key]));
        }
      }
      if (values.length > maxRounds) maxRounds = values.length;
      const total = values.reduce((sum, v) => sum + v, 0);
      if (total > maxTotal) maxTotal = total;
    });
    const rows = players.map((player) => {
      let values = Array.isArray(player.cards) ? player.cards.map(Number) : [];
      if (!values.length) {
        for (let i = 1; i <= maxRounds; i++) {
          const key = `Round${i}`;
          if (player[key] !== undefined && player[key] !== null && player[key] !== '' && !isNaN(Number(player[key]))) values.push(Number(player[key]));
        }
      }
      values = values.filter((v) => v !== null && v !== undefined && v !== '' && !isNaN(v));
      const total = values.reduce((sum, v) => sum + v, 0);
      return { playerId: player.playerId, name: player.name, values, total: values.length ? total : null };
    });
    const winnerIds = rows.filter((r) => r.total === maxTotal && maxTotal > -Infinity).map((r) => r.playerId);
    return { rows, maxRounds, winnerIds };
  }, [players]);

  const winners = useMemo(() => {
    if (!Array.isArray(players) || !Array.isArray(scoreboardRows?.winnerIds)) return [];
    const ids = new Set(scoreboardRows.winnerIds);
    return players.filter((p) => ids.has(p.playerId));
  }, [players, scoreboardRows?.winnerIds]);

  const winnerBestScore = useMemo(() => {
    if (!scoreboardRows || !Array.isArray(scoreboardRows.rows) || !Array.isArray(scoreboardRows.winnerIds)) {
      return null;
    }
    const idSet = new Set(scoreboardRows.winnerIds);
    const row = scoreboardRows.rows.find((r) => idSet.has(r.playerId));
    return (row && typeof row.total === 'number') ? row.total : null;
  }, [scoreboardRows]);

  const overlayFeature = isFeatureOn('revealOverlay');
  const [overlayComplete, setOverlayComplete] = useState(false);
  const hasResults = (engine?.round?.phase ?? phase) === 'revealed' || (engine?.round?.phase ?? phase) === 'expired';
  useEffect(() => { if (!hasResults) setOverlayComplete(false); }, [hasResults, stageGame?.key]);
  const overlayOpen = overlayFeature && (hasResults || winners.length > 0) && !overlayComplete;
  const [winnerPopupOpen, setWinnerPopupOpen] = React.useState(false);

  // If the game has moved beyond the lobby, ensure the wizard is closed
  useEffect(() => {
    if (wizardStep && phase !== 'lobby') {
      setWizardStep(null);
      setWizardSeen(true);
    }
    if (guestWizardStep && phase !== 'lobby') {
      setGuestWizardStep(null);
      setGuestWizardSeen(true);
    }
  }, [wizardStep, guestWizardStep, phase]);

  const scoreboardMessage = useMemo(() => {
    if (!players.length) return 'Waiting for players to join.';
    if (!stageGame?.key) return 'Waiting for the host to pick a game.';
    if ((engine?.round?.phase ?? phase) === 'locked') return isHost ? 'Ready to start when you are.' : 'Waiting for the host to start the round.';
    if ((engine?.round?.phase ?? phase) === 'in_progress') return 'Round in progress...';
    return 'Round results will appear here once revealed.';
  }, [players.length, stageGame?.key, engine?.round?.phase, phase, isHost]);

  const handleWizardClose = useCallback(() => {
    setWizardStep(null);
    setWizardSeen(true);
  }, []);

  const handleGuestWizardClose = useCallback(() => {
    setGuestWizardStep(null);
    setGuestWizardSeen(true);
  }, []);

  const handleSelectGame = useCallback(
    (key) => {
      if (!isHost || !key || key === selectedGameKey) return;
      const defaults = buildDefaultConfig(key);
      setGameConfig(defaults);
      setGameConfigKey(key);
      setRoomState((previous) => {
        const snapshot = previous ?? {};
        const baseRoom = snapshot.room ?? snapshot ?? {};
        const nextGame = { ...(baseRoom.game || {}), key, name: gameLookup.get(key)?.name ?? baseRoom.game?.name, summary: gameLookup.get(key)?.summary ?? baseRoom.game?.summary ?? '' };
        const nextRoom = { ...baseRoom, game: nextGame };
        if (snapshot.room) return { ...snapshot, room: nextRoom };
        return { ...snapshot, ...nextRoom };
      });
    },
    [buildDefaultConfig, gameLookup, isHost, selectedGameKey]
  );

  const handleAwardChange = useCallback((value) => { if (isHost) { setToolbarAward(value); setAwardDirty(true); } }, [isHost]);

  const runOperation = useCallback(
    async (operation, params = {}, errorLabel = 'perform action') => {
      if (!roomId) return { success: false, skipped: true, error: 'Room not ready' };
      const { silentError, ...operationParams } = params;
      setHostActionLoading(true);
      if (!silentError) setError('');
      let skipRefetch = false;
      let errorMessage = '';
      let payload = null;
      try {
        const actionOperations = new Set(['lock', 'start', 'remove', 'leave']);
        let requestName = operation;
        let requestParams = { roomId, playerId, ...operationParams };
        if (actionOperations.has(operation)) {
          requestName = 'action';
          requestParams = { ...requestParams, type: operation, by: requestParams.by ?? roomHostId ?? playerId };
        }
        
        payload = await firestoreApi(requestName, requestParams);
        applyRoomResult(payload);
      } catch (err) {
        const message = err?.message || '';
        errorMessage = message;
        if (!silentError) setError(message || `Failed to ${errorLabel}.`);
      } finally {
        setHostActionLoading(false);
      }
      return { success: !errorMessage, skipped: skipRefetch, error: errorMessage, payload };
    },
    [applyRoomResult, roomHostId, playerId, roomId]
  );

  if (!session) {
    if (sessionLoading) {
      return (
        <div className="section-entry" role="status">
          <h2 className="section-header">Loading Room...</h2>
          <p className="block-error">Fetching room info. Please wait.</p>
        </div>
      );
    }
    return (
      <div className="section-entry" role="alert">
        <h2 className="section-header">Room Session Not Found</h2>
        <p className="block-error">Your room session has expired or is missing.</p>
        <button type="button" className="control-button" onClick={() => navigate('/whoispaying', { replace: true })}>Return to Setup</button>
      </div>
    );
  }

  // Check if user has a valid player ID (is a member)
  const isValidMember = Boolean(session?.playerId);
  
  if (!isValidMember && !sessionLoading) {
    return (
      <div className="section-entry" role="alert">
        <h2 className="section-header">Not a Member</h2>
        <p className="block-error">
          You are not a member of this room. This can happen if you refreshed the page or opened a direct link.
          Please rejoin the room using the code: <strong>{roomId}</strong>
        </p>
        <button 
          type="button" 
          className="control-button" 
          onClick={() => navigate('/whoispaying', { replace: true, state: { roomId } })}
        >
          Rejoin Room
        </button>
      </div>
    );
  }

  // Show award immediately after room creation or join
  const stageAward = roomAward ?? toolbarAward ?? session?.award ?? '';
  const heroAward = (roomAward ?? toolbarAward ?? session?.award ?? '').trim();

  // Debug: log raw players list
  React.useEffect(() => {
    if (playersLoaded) {
      console.log('[WhoIsPayingRoom] Players loaded:', players);
    }
  }, [playersLoaded, players]);

  // Guest onboarding wizard: show once for non-host members in lobby
  useEffect(() => {
    if (
      !isHost &&
      isValidMember &&
      phase === 'lobby' &&
      !guestWizardStep &&
      !guestWizardSeen
    ) {
      setGuestWizardStep(1);
      setGuestWizardSeen(true);
    }
  }, [isHost, isValidMember, phase, guestWizardStep, guestWizardSeen]);

  const wizardHostName = hostName;
  const wizardAward = heroAward || stageAward || '';
  const wizardRoomCode = roomId;
  const wizardInviteUrl = React.useMemo(() => {
    if (!wizardRoomCode || typeof window === 'undefined') return '';
    try {
      return `${window.location.origin}/whoispaying?autoJoin=true&roomCode=${wizardRoomCode}`;
    } catch {
      return '';
    }
  }, [wizardRoomCode]);

  const wizardShareText = React.useMemo(() => {
    if (!wizardInviteUrl) return '';
    const funAward = wizardAward ? `🏆 ${wizardAward}` : 'a fun decision together';
    const friendlyHost = wizardHostName || 'A friend';
    const lines = [
      `Hey, ${friendlyHost} wants to decide: ${funAward}`,
      `Join this free Who is Paying game and add your name:`,
      wizardInviteUrl,
      `🥳 No downloads. Just tap, join, and see who wins!`
    ];
    return lines.join('\n');
  }, [wizardInviteUrl, wizardAward, wizardHostName]);

  return (
    <>
      <GlobalNav />
      <div className="section-room">
        {/* DEBUG: Show raw player list for diagnosis (development only) */}
        {showDebugPlayers && (
          <div style={{ background: 'transparent', color: '#ffffff', padding: '8px', marginBottom: '8px', border: '1px solid #ffffff', borderRadius: '6px', fontSize: '0.95em' }}>
            <strong>Debug: Raw Players List</strong>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(players, null, 2)}</pre>
          </div>
        )}
        <div className="block-room-shell">
        <header className="block-room-hero">
          <div className="block-room-hero-top hero-mobile-center">
            <div className="block-room-title hero-title-mobile-center">
              <h2 className="section-header">Who is Paying?</h2>
              <p className="block-room-note">Share the code below so your friends can join the room.</p>
            </div>
            {viewport?.width && viewport.width <= 600 ? (
              <div className="block-room-hero-tags section-room-meta" style={{ width: '100%' }}>
                <ul className="block-meta-list" aria-label="Room quick info" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                  <li className="block-meta-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <span className="block-meta-label">Room</span>
                    <span className="block-meta-value">
                      <span
                        data-testid="room-code"
                        className={`room-code-clickable${copiedRoom ? ' is-copied' : ''}`}
                        onClick={handleCopyRoom}
                        style={{ cursor: roomId ? 'pointer' : 'default' }}
                        aria-label={roomId ? `Copy room code ${roomId}` : 'Room code not available'}
                        title={copiedRoom ? 'Copied' : 'Copy'}
                      >
                        {copiedRoom ? <span style={{marginRight:4}}>✔</span> : null}
                        {copiedRoom ? 'Copied!' : (roomId ?? '--')}
                      </span>
                    </span>
                  </li>
                  <li className="block-meta-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="block-meta-label">Players</span>
                    <span className="block-meta-value" data-testid="player-count" aria-live="polite">{playerCountDisplay}</span>
                  </li>
                  <li className="block-meta-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="block-meta-label">Status</span>
                    <span className="block-meta-value">{phaseLabel}</span>
                  </li>
                  {expiresAt && (
                    <li className="block-meta-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="block-meta-label">Expires</span>
                      <span className="block-meta-value">
                        <ExpirationCountdown expiresAt={expiresAt} showLabel={false} />
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <div className="block-room-hero-tags hero-tags-mobile-2row">
                <div className="hero-chip-row">
                  <div className="room-chip" role="group" aria-label="Room code">
                    <span className="room-chip-label">Room</span>
                    <span 
                      className={`room-chip-value${copiedRoom ? ' is-copied' : ''}`}
                      data-testid="room-code"
                      onClick={handleCopyRoom}
                      style={{ cursor: roomId ? 'pointer' : 'default' }}
                      aria-label={roomId ? `Copy room code ${roomId}` : 'Room code not available'}
                      title={copiedRoom ? 'Copied' : 'Copy'}
                    >
                      {copiedRoom ? 'Copied!' : (roomId ?? '--')}
                    </span>
                    <span className="visually-hidden" role="status" aria-live="polite">{copiedRoom ? 'Room code copied' : ''}</span>
                  </div>
                  <div className="room-chip" role="text" aria-label="Status">
                    <span className="room-chip-label">Status</span>
                    <span className="room-chip-value">{phaseLabel}</span>
                  </div>
                  <div className="room-chip" role="text" aria-label="Players in room">
                    <span className="room-chip-label">Players</span>
                    <span className="room-chip-value" data-testid="player-count" aria-live="polite">{playerCountDisplay}</span>
                  </div>
                </div>
                <div className="hero-chip-row">
                  {expiresAt && (
                    <div className="room-chip" role="text" aria-label="Expiration countdown">
                      <span className="room-chip-label">Expires</span>
                      <span className="room-chip-value"><ExpirationCountdown expiresAt={expiresAt} showLabel={false} /></span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="room-chip share-chip"
                    aria-label="Share game"
                    onClick={() => {
                      const shareUrl = `${window.location.origin}/whoispaying?autoJoin=true&roomCode=${roomId}`;
                      const funAward = heroAward ? `🏆 ${heroAward}` : '';
                      const funLines = [
                        `Let's decide who pays for pizza (or anything)!`,
                        funAward ? `Prize: ${funAward}` : '',
                        `Join my Who is Paying game and enter your name to join:`,
                        shareUrl
                      ].filter(Boolean);
                      const shareText = funLines.join('\n');
                      if (navigator.share) {
                        navigator.share({ title: 'Who is Paying', text: shareText, url: shareUrl }).catch(() => {});
                      } else if (navigator.clipboard) {
                        navigator.clipboard.writeText(shareText);
                        alert('Share text copied!');
                      }
                    }}
                    disabled={phase !== 'lobby'}
                  >
                    <span className="room-chip-label">Share</span>
                    <span className="room-chip-value" aria-hidden="true" style={{fontSize:'1.1em'}}>🔗</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="block-room-timing hero-timing-mobile-center">
            <span><strong>Your Role:</strong> {isHost ? 'Host' : 'Guest'}</span>
            {heroAward && (
              <span>
                <strong>Winner:</strong> <span role="img" aria-label="Trophy" style={{ marginRight: 4 }}>🏆</span>{heroAward}
              </span>
            )}
            <span><strong>Host:</strong> {hostName}</span>
          </div>
        </header>

        {isHost && (
          <GameToolbar
            games={games}
            players={players}
            selectedKey={selectedGameKey ?? undefined}
            onSelectGame={isHost ? handleSelectGame : undefined}
            award={toolbarAward}
            onAwardChange={isHost ? handleAwardChange : undefined}
            config={displayConfig}
            configSchema={activeGameSchema}
            onConfigChange={isHost ? handleGameConfigChange : undefined}
            phase={phase}
            loading={hostActionLoading}
            actions={{
              lock: () => runOperation('lock', {
                game: {
                  key: selectedGameKey,
                  name: selectedGameEntry?.name || 'Unknown Game',
                  summary: selectedGameEntry?.summary || ''
                },
                config: gameConfig
              }, 'lock the table'),
              start: () => runOperation('start', {}, 'start the round')
            }}
            isHost={isHost}
          />
        )}

        <div className="block-room-main">
          <section className="section-results" aria-label="Game Results">
            {playersLoaded && computedPlayerCount < 2 && (
              <div className="block-room-waiting-note" style={{textAlign:'center',margin:'12px 0',color:'var(--text-secondary)'}}>
                Waiting for players to join...
              </div>
            )}
            {/* Winner popup shown after reveal overlay is closed */}
            <WinnerPopup
              open={winnerPopupOpen && winners.length > 0}
              winners={winners.map((w) => ({ playerId: w.playerId, name: w.name }))}
              award={stageAward || ''}
              score={winnerBestScore}
              onClose={() => setWinnerPopupOpen(false)}
            />

            <div className="block-results-header mobile-stack">
              <h3 className="section-results-title">
                {(() => {
                  if (!room?.game || !room?.game.key || phase === 'lobby') {
                    return 'Waiting for host to select and lock a game...';
                  }
                  if (typeof room?.game === 'string' && room?.game.trim()) {
                    return `Game: ${room.game}`;
                  }
                  return `Game: ${stageGame?.name ?? 'No game selected'}`;
                })()}
              </h3>
              <div className="block-results-actions">
                {stageGame && (phase !== 'lobby') && (
                  <button type="button" className="control-view-rules" aria-label="View Game Rules" onClick={() => setRulesOpen(true)}>Rules</button>
                )}
                <button type="button" className="control-view-rules" aria-label="Leave Room" onClick={() => navigate('/whoispaying', { replace: true })}>Leave Room</button>
              </div>
            </div>

            <div className="block-results-table responsive-table">
              <div className="results-desktop-table-wrapper">
                <table className="results-desktop-table">
                  <thead>
                    <tr>
                      <th className="block-results-player">Player</th>
                      {Array.from({ length: (room?.game?.Number_of_rounds || scoreboardRows.maxRounds || 1) }, (_, i) => (
                        <th key={i} className="block-results-round">Round {i + 1}</th>
                      ))}
                      <th className="block-results-total">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player) => {
                      const row = scoreboardRows.rows.find((r) => r.playerId === player.playerId);
                      const isWinner = scoreboardRows.winnerIds.includes(player.playerId);
                      return (
                        <tr key={player.playerId} className={`block-results-row${isWinner ? ' winner-row' : ''}`}>
                          <td className="block-results-player-name">{isWinner && <span className="results-desktop-winner" aria-label="Winner">🏆</span>}{player.name}</td>
                          {Array.from({ length: (room?.game?.Number_of_rounds || scoreboardRows.maxRounds || 1) }, (_, i) => (
                            <td key={i} className="block-results-cell">{row && row.values[i] != null ? row.values[i] : <span className="results-mobile-placeholder">--</span>}</td>
                          ))}
                          <td className="block-results-total-cell">{row && row.total != null ? row.total : <span className="results-mobile-placeholder">--</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="results-mobile-list">
                {players.map((player) => {
                  const row = scoreboardRows.rows.find((r) => r.playerId === player.playerId);
                  const isWinner = scoreboardRows.winnerIds.includes(player.playerId);
                  return (
                    <div key={player.playerId} className={`results-mobile-card${isWinner ? ' winner-row' : ''}`}>
                      <div className="results-mobile-header">
                        <span className="results-mobile-player">{player.name}</span>
                        {isWinner && <span className="results-mobile-winner">🏆</span>}
                      </div>
                      <div className="results-mobile-rounds">
                        {Array.from({ length: scoreboardRows.maxRounds ?? 1 }, (_, i) => (
                          <span key={i} className="results-mobile-round">
                            <span className="results-mobile-round-label">R{i + 1}:</span>
                            <span className="results-mobile-round-value">{row && row.values[i] != null ? row.values[i] : <span className="results-mobile-placeholder">--</span>}</span>
                          </span>
                        ))}
                        <span className="results-mobile-total">Total: {row && row.total != null ? row.total : <span className="results-mobile-placeholder">--</span>}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        {overlayOpen && (
          <RoundRevealOverlay
            open={overlayOpen}
            mode={stageGame?.key === 'cards' ? 'cards' : 'dice'}
            players={Array.isArray(scoreboardRows?.rows) ? scoreboardRows.rows.map((r) => ({ playerId: r.playerId, name: r.name, values: Array.isArray(r.values) ? r.values : [], total: r.total ?? 0 })) : []}
            winners={winners.map((w) => ({ playerId: w.playerId, name: w.name }))}
            viewport={viewport}
            viewerPlayerId={playerId}
            onComplete={() => { setOverlayComplete(true); setWinnerPopupOpen(true); }}
          />
        )}

        {isHost && wizardStep && (
          <div className="block-wizard-overlay" role="dialog" aria-modal="true" aria-label="Room setup wizard">
            <div className="block-wizard-backdrop" onClick={handleWizardClose} />
            <div className="block-wizard-panel" role="document">
              <header className="block-wizard-header">
                <div className="block-wizard-header-main">
                  <div className="block-wizard-step-label">
                    Step {wizardStep} of 4
                  </div>
                  <div className="block-wizard-step-title">
                    {wizardStep === 2 && 'Your name & room'}
                    {wizardStep === 3 && 'Invite your friends'}
                    {wizardStep === 4 && 'Wait for everyone to join'}
                  </div>
                </div>
                <button
                  type="button"
                  className="control-wizard-close"
                  onClick={handleWizardClose}
                  aria-label="Close setup wizard"
                >
                  ✕
                </button>
              </header>

              <div className="block-wizard-body">
                {wizardStep === 2 && (
                  <div className="block-wizard-step block-wizard-step--summary">
                    <p className="block-wizard-text">
                      This is what your friends will see when they join.
                    </p>
                    <div className="block-wizard-preview-card">
                      <div className="block-wizard-preview-line">
                        <span className="block-wizard-preview-label">Host</span>
                        <span className="block-wizard-preview-value">{wizardHostName || 'Host'}</span>
                      </div>
                      <div className="block-wizard-preview-line">
                        <span className="block-wizard-preview-label">Decision</span>
                        <span className="block-wizard-preview-value">{wizardAward || 'What you are deciding'}</span>
                      </div>
                    </div>
                    <div className="block-wizard-footer">
                      <button
                        type="button"
                        className="control-primary"
                        onClick={() => setWizardStep(3)}
                      >
                        Next: Invite friends
                      </button>
                    </div>
                  </div>
                )}

                {wizardStep === 3 && (
                  <div className="block-wizard-step block-wizard-step--invite">
                    <p className="block-wizard-text">
                      Share this link or code so your friends can join.
                    </p>
                    <div className="block-wizard-invite-card">
                      <div className="block-wizard-room-code" aria-label="Room code and share link">
                        <span className="block-wizard-room-code-label">Room code</span>
                        <button
                          type="button"
                          className="control-code"
                          onClick={handleCopyRoom}
                          disabled={!wizardRoomCode}
                        >
                          {wizardRoomCode || '--'}
                        </button>
                        <button
                          type="button"
                          className="control-secondary"
                          onClick={async () => {
                            if (!wizardInviteUrl) return;
                            try {
                              if (navigator.share) {
                                await navigator.share({
                                  title: 'Who is Paying',
                                  text: wizardShareText || wizardInviteUrl,
                                  url: wizardInviteUrl
                                });
                                setWizardCopied(false);
                              } else if (navigator.clipboard?.writeText) {
                                await navigator.clipboard.writeText(wizardShareText || wizardInviteUrl);
                                setWizardCopied(true);
                                setTimeout(() => setWizardCopied(false), 2000);
                              }
                            } catch {
                              // ignore share cancellation/errors
                            }
                          }}
                        >
                          Share link
                        </button>
                        {wizardCopied && (
                          <span className="block-wizard-copy-note" aria-live="polite">
                            Link copied!
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="block-wizard-footer">
                      <button
                        type="button"
                        className="control-primary"
                        onClick={() => setWizardStep(4)}
                      >
                        Next: Wait for players
                      </button>
                    </div>
                  </div>
                )}

                {wizardStep === 4 && (
                  <div className="block-wizard-step block-wizard-step--game">
                    <p className="block-wizard-text">
                      Waiting for friends to join. You need at least you plus one more player to start.
                    </p>
                    <div className="block-wizard-preview-card">
                      <div className="block-wizard-preview-line">
                        <span className="block-wizard-preview-label">Players joined</span>
                        <span className="block-wizard-preview-value">{playerCountDisplay}</span>
                      </div>
                    </div>
                    {players.length > 0 && (
                      <ul className="block-wizard-player-list" aria-label="Players in the room">
                        {players.map((p) => (
                          <li key={p.playerId || p.name} className="block-wizard-player-item">
                            {p.name || 'Player'}
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="block-wizard-footer">
                      <button
                        type="button"
                        className="control-primary"
                        disabled={!playersLoaded || computedPlayerCount < 2}
                        onClick={handleWizardClose}
                      >
                        Continue to room
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!isHost && guestWizardStep && (
          <div className="block-wizard-overlay" role="dialog" aria-modal="true" aria-label="Guest onboarding">
            <div className="block-wizard-backdrop" onClick={handleGuestWizardClose} />
            <div className="block-wizard-panel" role="document">
              <header className="block-wizard-header">
                <div className="block-wizard-header-main">
                  <div className="block-wizard-step-label">
                    Step {guestWizardStep} of 2
                  </div>
                  <div className="block-wizard-step-title">
                    {guestWizardStep === 1 && 'Welcome to the room'}
                    {guestWizardStep === 2 && 'How this game works'}
                  </div>
                </div>
                <button
                  type="button"
                  className="control-wizard-close"
                  onClick={handleGuestWizardClose}
                  aria-label="Close guest onboarding"
                >
                  ✕
                </button>
              </header>

              <div className="block-wizard-body">
                {guestWizardStep === 1 && (
                  <div className="block-wizard-step block-wizard-step--guest-intro">
                    <p className="block-wizard-text">
                      Hi <strong>{session?.displayName || 'there'}</strong>, you’re in the room.
                      The host <strong>{wizardHostName || 'Host'}</strong> chose the winner action{' '}
                      {wizardAward
                        ? <strong>{wizardAward}</strong>
                        : <span>a fun reward</span>
                      }.
                    </p>
                    <div className="block-wizard-footer">
                      <button
                        type="button"
                        className="control-primary"
                        onClick={() => setGuestWizardStep(2)}
                      >
                        Next: How it works
                      </button>
                    </div>
                  </div>
                )}

                {guestWizardStep === 2 && (
                  <div className="block-wizard-step block-wizard-step--guest-rules">
                    <div className="game-icons-row-compact" aria-hidden="true">
                      <span className="game-icon-compact">🎲</span>
                      <span className="game-icon-divider">or</span>
                      <span className="game-icon-compact">🃏</span>
                    </div>
                    <p className="block-wizard-text">
                      You’ll get up to five rolls or five cards. Each one adds to your total.
                    </p>
                    <p className="block-wizard-text">
                      After everyone reveals, the highest score wins the winner action{' '}
                      {wizardAward ? <strong>{wizardAward}</strong> : <strong>for the group</strong>}.
                    </p>
                    <div className="block-wizard-footer">
                      <button
                        type="button"
                        className="control-primary"
                        onClick={handleGuestWizardClose}
                      >
                        Got it, let’s play
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <RulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} game={stageGame} />
        {syncStatus === 'error' && (
          <div className="block-error" role="alert" aria-live="assertive" style={{ cursor: 'pointer' }} onClick={() => { setSyncStatus('ok'); fetchState({ silent: false }); }}>
            Connection issue - tap to refresh (last sync: {Math.round((Date.now() - lastSync) / 1000)}s ago)
          </div>
        )}
        {error && <div className="block-error" role="alert" aria-live="assertive">{error}</div>}
      </div>
      <ExpiredNotice
        open={expiredOpen}
        onConfirm={() => navigate('/whoispaying', { replace: true })}
        onTimeout={() => navigate('/whoispaying', { replace: true })}
        durationMs={15000}
      />
      </div>
    </>
  );
}

